import { useRef, useState, useEffect, useCallback, type RefObject } from 'react';
import type { KeystrokeCallback } from './useTypingSession';

export type HeatmapData = ReadonlyMap<string, number>;

const FLUSH_INTERVAL_MS = 500;

/**
 * Tracks keystroke frequency with zero per-keystroke React overhead.
 *
 * Architecture:
 *   - countsRef: plain Map mutated directly on each keystroke — no renders
 *   - heatmap state: flushed from countsRef every FLUSH_INTERVAL_MS
 *   - callbackRef: stable ref passed to useTypingSession as onKeystrokeRef
 *
 * The component only re-renders at most twice per second regardless of WPM.
 */
export function useKeystrokeHeatmap(): {
  callbackRef: RefObject<KeystrokeCallback | null>;
  heatmap: HeatmapData;
  reset: () => void;
} {
  // Raw counts — mutated directly, never triggers renders
  const countsRef = useRef<Map<string, number>>(new Map());

  // Flushed snapshot — this is what the component reads
  const [heatmap, setHeatmap] = useState<HeatmapData>(new Map());

  // Stable callback ref handed to useTypingSession
  const callbackRef = useRef<KeystrokeCallback | null>(null);

  useEffect(() => {
    callbackRef.current = (key: string) => {
      const map = countsRef.current;
      map.set(key, (map.get(key) ?? 0) + 1);
    };
    return () => { callbackRef.current = null; };
  }, []);

  // Batch flush every 500ms
  useEffect(() => {
    const id = setInterval(() => {
      if (countsRef.current.size === 0) return;
      setHeatmap(new Map(countsRef.current));
    }, FLUSH_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  const reset = useCallback(() => {
    countsRef.current.clear();
    setHeatmap(new Map());
  }, []);

  return { callbackRef, heatmap, reset };
}
